import { EmployeeRole, EmployeeType, IEmployee, IInviteEmployee, SiteRole } from 'types';

/**
 * Returns a string to display on the UI based on the employee type
 *
 * @param {EmployeeType} type - The type of the employee.
 * @returns {string} - Returns the string to display corresponding to the EmployeeType.
 *                     'User' for EmployeeType.USER and 'Org Admin' for EmployeeType.ADMIN.
 */
export const employeeTypeDisplay = (type: EmployeeType): string => {
  const userType: Record<EmployeeType, string> = {
    [EmployeeType.USER]: 'User',
    [EmployeeType.ADMIN]: 'Org Admin',
  };
  return userType[type];
};

/**
 * Returns a string to display on the UI for a given site role.
 *
 * @param {SiteRole} role - The role at a given site.
 *
 * @returns {string} - Returns the string to display corresponding to the site role.
 *                     'Employee' for SiteRole.EMPLOYEE, 'Manager' for SiteRole.MANAGER, and 'Site Admin' for SiteRole.ADMIN.
 */
export const roleSiteDisplay = (role: SiteRole): string => {
  const userType: Record<SiteRole, string> = {
    [SiteRole.EMPLOYEE]: 'Employee',
    [SiteRole.MANAGER]: 'Manager',
    [SiteRole.ADMIN]: 'Site Admin',
  };
  return userType[role];
};

/**
 * Returns a string to display on the UI based on an employee's type or role at a given site.
 *
 * @param {EmployeeType} type - The type of the employee.
 * @param {SiteRole} [role] - The role at a given site. Optional.
 *
 * @returns {string} - Returns the string to display according to the employee's type or role.
 *                     If the employee type is USER and a site role is provided, it returns the display string based on the role at a given site.
 *                     Otherwise, it returns the display string for the employee type.
 */
export const orgRoleDisplay = (type: EmployeeType, role?: SiteRole): string => (type === EmployeeType.USER && role ? roleSiteDisplay(role) : employeeTypeDisplay(type));

/**
 * Checks if an employee is an Organization Admin.
 *
 * @param {IEmployee | IInviteEmployee} employee - The employee object containing the employee type.
 * @returns {boolean} - Returns `true` if the employee's type is `EmployeeType.ADMIN`, otherwise `false`.
 */
export const isOrgAdmin = (employee: IEmployee | IInviteEmployee): boolean => employee.type === EmployeeType.ADMIN;

/**
 * A mapping of site roles to employee roles.
 *
 * @type {Record<SiteRole, EmployeeRole>}
 *
 * Maps each `SiteRole` to its corresponding `EmployeeRole`.
 * - `SiteRole.ADMIN` maps to `EmployeeRole.SITE_ADMIN`
 * - `SiteRole.MANAGER` maps to `EmployeeRole.MANAGER`
 * - `SiteRole.EMPLOYEE` maps to `EmployeeRole.EMPLOYEE`
 */
export const siteRoleToEmployeeRole: Record<SiteRole, EmployeeRole> = {
  [SiteRole.ADMIN]: EmployeeRole.SITE_ADMIN,
  [SiteRole.MANAGER]: EmployeeRole.MANAGER,
  [SiteRole.EMPLOYEE]: EmployeeRole.EMPLOYEE,
};
